import React, { useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { amber } from '@material-ui/core/colors';
import { TextField, Typography, Grid, } from '@material-ui/core';
import { Button } from '@material-ui/core';
import classes from './SignIn.module.css'
import logo from '../../../img/geetee.png';
import { firestore, functions } from '../../../firebase/firebase';
import LoadingSpinner from '../../../Public/components/LoadingSpinner/LoadingSpinner';
import { useAuth } from '../../../providers/AuthContext';

const customTheme = createTheme({
    overrides: {
        MuiOutlinedInput: {
            root: {
                '&$focused $notchedOutline': {
                    borderColor: amber[500],
                },
                '&:hover $notchedOutline': {
                    borderColor: amber[500],
                },
            },
            notchedOutline: {
                borderColor: 'grey'
            },
            input: {
                color: "white"
            },
        },
        MuiFormLabel: {
            root: {
                color: amber[500],
                '&$focused ': {
                    color: amber[500],
                },
            },
        },
    }
});

const SignIn = (props) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
    const [totp, setTotp] = useState(null);
	const [totps, setTotps] = useState('');
    const [usedRecoveryCode, setUsedRecoveryCode] = useState(false);
    const [type, setType] = useState('None');
    const [docId, setDocId] = useState('');

    const { login } = useAuth();

	const validateCode = functions.httpsCallable('validateTotpCode');
    const sendCode = functions.httpsCallable('sendMFA');
	const sendMemo = functions.httpsCallable('sendOutMemo');

    const MINUTES = 15;

    useEffect(() => {
		if (error) {
			const timeout = setTimeout(() => setError(null), 3000);
			return () => clearTimeout(timeout);
		}
	}, [error]);

    const fetchUserData = async (email) => {
		const userQuery = await firestore
			.collection("users")
			//.where("companyID", "==", companyID)
			.where("email", "==", email)
			.get();
	
		if (userQuery.empty) {
			setError("User not found");
			return null;
		}
	
		const userDoc = userQuery.docs[0];
		const user = userDoc.data();

        setType(user?.mfaType || 'SMS');
        setTotp(user?.totpObj);
		setTotps(user?.totp?.auth_url);
		setDocId(userDoc.id);
		setPhone(user.phone || '');

		return user;
	};

    const generateCode = async (docId, phone, email, type) => {
		const code = Math.floor(100000 + Math.random() * 900000).toString();
		await firestore.collection('users').doc(docId).update({
			mfaCode: code,
			mfaCodeExpires: new Date(Date.now() + MINUTES * 60000),
		});

		if (type === 'SMS') 	await sendSMS(code, phone)
		if (type === 'Email') 	await sendEmail(email, code)
	};

    const handleMFA = async (user) => {
		// Check if MFA is required
		if (user.mfaEnabled) {
			// If user hasn't entered a code yet, generate one and stop execution
			if (!requiresTwoFactor) {
				setRequiresTwoFactor(true);
				if (user.mfaType === "Email" || user.mfaType === "SMS" || user.mfaType === 'GeeTeeApp') {
					await generateCode(user.docId, user.phone, user.email, user.mfaType);
				}
				return false; // Wait for user input
			}
	
			// Verify MFA code
			if (user.mfaType === "Email" || user.mfaType === "SMS" || user.mfaType === 'GeeTeeApp') {
				const isValid = await verifyEmailMFA(docId, code);
				if (!isValid) return false;
			} else if (user.mfaType === "MobileAuth" && !usedRecoveryCode) {
				const isValid = await verifyMobileAuthMFA(totp, code, totps);
				if (!isValid) return false;
			} else if (user.mfaType === "MobileAuth" && usedRecoveryCode) {
				// Recovery code was successfully used, so we allow login to proceed
				return true;
			}
		}
	
		return true; // MFA Passed or not required
	};

    const verifyEmailMFA = async (docId, code) => {
		const userData = await firestore.collection("users").doc(docId).get();
		const storedCode = userData.data().mfaCode;
		const expiration = userData.data().mfaCodeExpires?.toDate();
	
		if (!storedCode || new Date() > expiration || code !== storedCode) {
			setError("Invalid or expired MFA code");
			setCode("");
			return false;
		}
	
		// Clear MFA code from Firestore
		await firestore.collection("users").doc(docId).update({
			mfaCode: null,
			mfaCodeExpires: null,
		});
	
		setError("");
		return true;
	};

    const handleSignIn = async (event) => {
		event.preventDefault();
		setError(""); // Reset errors before starting
	
		try {
			// Step 1: Validate User
			const user = await fetchUserData(email.toLowerCase());
			if (!user) return;
	

			// Step 3: Handle MFA Requirement
			const mfaPassed = await handleMFA(user);
			if (!mfaPassed) return;
	
			// Step 4: Authenticate User
			await authenticateUser(email, password);
	
		} catch (error) {
			console.error(error);
			setError(error.message || "An error occurred");
		}
	};

    const verifyMobileAuthMFA = async (totp, code, totps) => {
		setLoading(true);
		try {
			const result = await validateCode({ totp: totp, code: code, url: totps });
			setLoading(false);
	
			if (!result.data.success) {
				setError("Invalid mobile authentication code");
				setCode("");
				return false;
			}
	
			setCode("");
			setError("");
			return true;
		} catch (error) {
			console.error(error);
			setLoading(false);
			setError("Invalid mobile authentication code");
			setCode("");
			return false;
		}
	};

    const verifyMobileAuthRecovery = async (code) => {
		setLoading(true);
		try {
			const userDoc = await firestore.collection('users').doc(docId).get();
			const user = userDoc.data();
			const recoveryCodes = user.totp.recoveryCodes;
	
			if (!recoveryCodes.includes(code)) {
				setError("Invalid recovery code");
				setLoading(false);
				setCode("");
				return false; // Stop execution if recovery code is invalid
			}
	
			// Remove used recovery code from Firestore
			const index = recoveryCodes.indexOf(code);
			recoveryCodes.splice(index, 1);
			await firestore.collection('users').doc(docId).update({
				'totp.recoveryCodes': recoveryCodes,
			});
	
			setLoading(false);
			setCode("");
			setError("");
			setUsedRecoveryCode(true); // Mark that recovery code was used
			return true; // Allow authentication to proceed
	
		} catch (error) {
			console.error(error);
			setLoading(false);
			setError("Invalid recovery code");
			setCode("");
			return false;
		}
	};

    const authenticateUser = async (email, password) => {
		const loginResult = await login(email, password).catch((err) => {
			setError("Invalid email or password");
			return null;
		});
	
		if (!loginResult) return false; // Stop if login failed
		return true;
	};


    // Step 2: Handle phone number submission
    const submitPhoneNumber = async () => {
        if (!phone || phone.trim() === '') {
            setError("Phone number is required");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Update user document with phone number
            await firestore.collection('users').doc(docId).update({
                phone: phone.trim()
            });

        } catch (error) {
            console.error('Phone submission error:', error);
            setError("Failed to save phone number");
        } finally {
            setLoading(false);
        }
    };


    // Helper: Send SMS
    const sendSMS = async (code, phoneNumber) => {
        try {
            await sendCode({
                sandbox: process.env.NODE_ENV === 'development',
                phone: phoneNumber.replace(/\s/g, '').replace(/-/g, '').replace(/\(/g, '').replace(/\)/g, ''),
                code: code
            });
        } catch (error) {
            console.error('SMS sending error:', error);
            throw new Error("Failed to send verification code");
        }
    };

	const sendEmail = async (email, code) => {
		try {
			await sendMemo({
				sandbox: process.env.NODE_ENV === 'development',
				email: email,
				subject: 'Your Sign-In Code',
				html: `Your sign-in code is: <p style="font-size:30px"><b>${code}</b></p>
				<br/><p>This code is valid for 15 minutes.</p>`,
				code: code
			});
		} catch (error) {
			console.error('Email sending error:', error);
			throw new Error("Failed to send verification code");
		}
	}

    // Resend MFA code
    const resendCode = async (type) => {
        if (!docId || !phone) {
            setError("Cannot resend code - missing information");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            if (type === 'SMS') {
				await generateCode(docId, phone, email, type);
			} else if (type === 'Email') {
				await generateCode(docId, phone, email, type);
			}
            setCode(''); // Clear the input
            setError(null); // Clear any errors
        } catch (error) {
            console.error('Resend error:', error);
            setError("Failed to resend code");
        } finally {
            setLoading(false);
        }
    };

	const onChangeHandler = (event) => {
		const { name, value } = event.target;
		if (name === 'userEmail') setEmail(value);
		else if (name === 'userCode') setCode(value);
		else if (name === 'userPassword') setPassword(value);
        else if (name === 'userPhone') setPhone(value);
		//else if (name === 'userCompany') setCompanyID(value);
	};

	return (
		<div className={classes.Container}>
			<img src={logo} className={classes.Logo} alt='Logo'></img>
			<ThemeProvider theme={customTheme}>
				{!requiresTwoFactor ? (
					<>
                    <TextField
                        type="email"
                        name="userEmail"
                        value={email}
                        onChange={onChangeHandler}
                        label="Email"
                        margin="normal"
                        variant="outlined"
                        fullWidth
                        autoComplete="off" // Prevent autofill
                    />

                    <TextField
                        type="password"
                        name="userPassword"
                        value={password}
                        onChange={onChangeHandler}
                        label="Password"
                        margin="normal"
                        variant="outlined"
                        fullWidth
                        autoComplete="current-password" // Allow autofill for password
                    />
					</>
				) : (
					<TextField
						name="userCode"
						value={code}
						onChange={onChangeHandler}
						label="Sign-In Code"
						margin="normal"
						variant="outlined"
						fullWidth
						InputProps={{
							endAdornment: (
								<React.Fragment>
									{loading ? <LoadingSpinner/> : null}
								</React.Fragment>
							),
						}}
					/>
				)}
			</ThemeProvider>

			<Grid container>
				<Grid container item justifyContent="center" xs={12}>
					<Button
						style={{ marginRight: '10px',}}
						variant="contained"
						disabled={loading}
						onClick={handleSignIn}
					>
						{requiresTwoFactor ? 'Submit Code' : 'Sign In'}
					</Button>
					{requiresTwoFactor && (type === 'Email'||type === 'SMS') && (
						<Button
							variant="contained"
							style={{ marginRight: '10px',  }}
							disabled={sending}
							onClick={async () => {
								setSending(true);
								try {
									await resendCode(type);
								} catch (error) {
									setError("Failed to resend code");
								} finally {
									setSending(false);
								}
							}}
						>
							Resend Code
						</Button>
					)}
					{requiresTwoFactor && type === 'MobileAuth' && (
						<Button
							variant="contained"
							style={{ marginRight: '10px',  }}
							disabled={loading}
							onClick={async () => {
								const recoverySuccess = await verifyMobileAuthRecovery(code);
								if (recoverySuccess) {
									await authenticateUser(email, password);
								}
							}}
						>
							Use Recovery Code
						</Button>
					)}
				</Grid>
                {requiresTwoFactor && type === 'Email' && (
                    <Grid container item justifyContent="center" xs={12}>
                        <Typography variant="body2" style={{ color: 'white',marginTop:'10px' }}>
                            {sending ? 'Sending...' : `Code sent to ${email}`}
                        </Typography>
                    </Grid>
                )}
			</Grid>

			{error && <div className={classes.ErrorMsg}>{error}</div>}
		</div>
	);
};

export default SignIn;